import type {
  ButtonHTMLAttributes,
  ComponentPropsWithRef,
  InputHTMLAttributes,
  ReactNode,
  TextareaHTMLAttributes,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from './Icon.js';
import { cn } from '../lib/cn.js';

/**
 * INFRA-006 — base component library.
 * Presentational only: no data fetching, no i18n keys baked in. Callers pass
 * already-translated strings, which keeps these reusable on the public pages
 * where no session exists.
 *
 * The classes are the design system's own utilities rather than bespoke CSS, so
 * a screen that only uses these primitives already looks like the rest of the
 * app and picks up the accent colour and the dark theme for free.
 */

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: 'sm' | 'md' | 'lg';
  block?: boolean;
  loading?: boolean;
  icon?: ReactNode;
}

const VARIANT: Record<Variant, string> = {
  primary: 'bg-primary text-primary-foreground hover:opacity-90',
  secondary: 'bg-muted text-foreground hover:bg-accent',
  ghost: 'bg-transparent text-foreground hover:bg-muted',
  danger: 'bg-destructive/10 text-destructive hover:bg-destructive/20',
  success: 'bg-success/10 text-success hover:bg-success/20',
};

const SIZE: Record<'sm' | 'md' | 'lg', string> = {
  sm: 'h-8 px-2.5 text-xs rounded-lg',
  md: 'h-9 px-3 text-sm rounded-lg',
  lg: 'h-11 px-5 text-sm rounded-xl',
};

export function Button({
  variant = 'secondary',
  size = 'md',
  block = false,
  loading = false,
  icon,
  children,
  className = '',
  disabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      className={cn(
        'inline-flex items-center justify-center gap-2 font-medium whitespace-nowrap transition-all active:scale-[0.97]',
        'disabled:opacity-50 disabled:pointer-events-none',
        VARIANT[variant],
        SIZE[size],
        block && 'w-full',
        className,
      )}
      disabled={disabled || loading}
      {...rest}
    >
      {loading ? <Spinner /> : icon}
      {children}
    </button>
  );
}

export function IconButton({
  label,
  badge,
  children,
  className = '',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { label: string; badge?: number; className?: string }) {
  return (
    <button
      className={cn(
        'relative h-9 w-9 shrink-0 rounded-lg flex items-center justify-center text-muted-foreground',
        'hover:bg-muted hover:text-foreground transition-colors',
        className,
      )}
      aria-label={label}
      title={label}
      {...rest}
    >
      {children}
      {badge !== undefined && badge > 0 && (
        <span className="absolute -top-0.5 -end-0.5 min-w-4 h-4 px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-semibold flex items-center justify-center ltr-nums">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </button>
  );
}

export function Card({
  title,
  actions,
  children,
  padded = true,
  className = '',
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  padded?: boolean;
  className?: string;
}) {
  return (
    <section className={cn('glass-card rounded-xl overflow-hidden', className)}>
      {(title || actions) && (
        <header className="flex items-center justify-between gap-2 px-4 py-3 border-b border-border/60">
          {title && <h2 className="text-sm font-semibold truncate">{title}</h2>}
          {actions && <div className="flex items-center gap-1.5 shrink-0">{actions}</div>}
        </header>
      )}
      {padded ? <div className="p-4">{children}</div> : children}
    </section>
  );
}

export function Field({
  label,
  error,
  hint,
  htmlFor,
  children,
}: {
  label?: string;
  error?: string;
  hint?: string;
  htmlFor?: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      {label && (
        <label className="block text-xs font-semibold text-muted-foreground" htmlFor={htmlFor}>
          {label}
        </label>
      )}
      {children}
      {error && <span className="block text-xs text-destructive">{error}</span>}
      {!error && hint && <span className="block text-[11px] text-muted-foreground">{hint}</span>}
    </div>
  );
}

/**
 * The shared field surface: a filled row that darkens slightly on focus.
 *
 * A tenth of the muted foreground is a tint you can see on a white card and
 * almost nothing on a dark one — 1.19:1 against the card behind it, with no
 * border to make up the difference, which is a form whose fields have to be
 * hunted for rather than read. Dark mode therefore gets a stronger fill and the
 * edge the palette already has a token for. The border is declared in both
 * modes and merely transparent in the light one, so the two are the same shape
 * and a field does not change height when the lights go out.
 */
export const FIELD =
  'w-full rounded-lg border border-transparent bg-muted-foreground/10 text-sm outline-none transition-colors placeholder:text-muted-foreground focus:bg-muted-foreground/[0.16] dark:border-input dark:bg-muted-foreground/[0.16] dark:focus:bg-muted-foreground/[0.22] aria-[invalid=true]:ring-2 aria-[invalid=true]:ring-destructive/40 disabled:opacity-60';

export function Input({
  invalid,
  className = '',
  ...rest
}: ComponentPropsWithRef<'input'> & { invalid?: boolean }) {
  return (
    <input
      className={cn(FIELD, 'h-10 px-3', className)}
      aria-invalid={invalid ? 'true' : undefined}
      {...rest}
    />
  );
}

export function Textarea({
  invalid,
  className = '',
  ...rest
}: TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }) {
  return (
    <textarea
      className={cn(FIELD, 'min-h-24 p-3 resize-y', className)}
      aria-invalid={invalid ? 'true' : undefined}
      {...rest}
    />
  );
}

/**
 * Re-exported rather than defined here: the dropdown stopped being a `<select>`
 * with classes on it and became a listbox with a portal, a keyboard map and a
 * roles contract (see Select.tsx). It still belongs in this file's public
 * surface, so every screen keeps importing it from the same place.
 */
export { Select, type SelectOption } from './Select.js';

export type BadgeTone = 'neutral' | 'success' | 'danger' | 'warning' | 'info' | 'primary';

const TONE: Record<BadgeTone, string> = {
  neutral: 'bg-muted text-muted-foreground',
  success: 'bg-success/10 text-success',
  danger: 'bg-destructive/10 text-destructive',
  warning: 'bg-warning/10 text-warning',
  info: 'bg-info/10 text-info',
  primary: 'bg-primary/10 text-primary',
};

export function Badge({ tone = 'neutral', children }: { tone?: BadgeTone; children: ReactNode }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap',
        TONE[tone],
      )}
    >
      {children}
    </span>
  );
}

/**
 * A score out of five, drawn. Read-only: the picker that collects a rating is a
 * row of buttons and belongs to the screen that owns the form.
 *
 * `label` is the whole meaning for anyone not looking at the screen — five
 * identical glyphs tell a screen reader nothing — so the stars themselves are
 * hidden and the caller passes an already-translated sentence, per this file's
 * no-i18n-inside rule.
 *
 * A fractional value rounds to the nearest whole star rather than drawing a
 * half: an average of 4.3 is written out next to this anyway, and a clipped
 * glyph at small sizes reads as a rendering fault.
 */
export function Stars({ value, label, size = 16 }: { value: number; label: string; size?: number }) {
  const filled = Math.round(value);
  return (
    <span className="inline-flex items-center gap-0.5 text-warning" role="img" aria-label={label}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Icon key={n} name="star" size={size} fill={n <= filled ? 'currentColor' : 'none'} />
      ))}
    </span>
  );
}

export function Spinner() {
  return (
    <span
      className="inline-block h-4 w-4 shrink-0 rounded-full border-2 border-current border-t-transparent animate-spin"
      role="status"
      aria-live="polite"
    />
  );
}

export function LoadingScreen() {
  const { t } = useTranslation();
  return (
    <div className="flex-1 flex items-center justify-center py-16">
      <div className="flex flex-col items-center gap-3 text-muted-foreground">
        <Spinner />
        <span className="text-sm">{t('common.loading')}</span>
      </div>
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  body,
  action,
}: {
  icon?: ReactNode;
  title: string;
  body?: string;
  action?: ReactNode;
}) {
  return (
    <div className="glass-card rounded-xl text-center py-10 px-4 space-y-2">
      {icon && (
        <span className="block text-2xl text-muted-foreground/60" aria-hidden>
          {icon}
        </span>
      )}
      <p className="text-sm font-medium">{title}</p>
      {body && <p className="text-xs text-muted-foreground max-w-sm mx-auto">{body}</p>}
      {action && <div className="pt-1">{action}</div>}
    </div>
  );
}

/** Renders a `RequestError` code (an i18n key) as translated text. */
export function ErrorNotice({ code }: { code: string }) {
  const { t } = useTranslation();
  return (
    <div
      className="rounded-lg bg-destructive/10 text-destructive text-xs font-medium px-3 py-2"
      role="alert"
    >
      {t(code, { defaultValue: t('errors.unknown') })}
    </div>
  );
}
